import { withImageVersion } from "./imageVersion";
import { saveOrShareBlob } from "./share";

/**
 * Build a 1080×1080 PNG "Share my Angel" social card and either trigger
 * a native share sheet (mobile) or a file download (desktop).
 *
 * Layout (warm-black background, brass accents):
 *   ┌──────────────────────────────────┐
 *   │   • Angel Squad                  │  small wordmark
 *   │                                  │
 *   │       [ Angel artwork ]          │  ~700×700 centered
 *   │                                  │
 *   │             042                  │  giant brass numeral
 *   │           for Jane Doe           │  italic serif
 *   │                                  │
 *   │       myangelsquad.com           │  tiny footer
 *   └──────────────────────────────────┘
 */
const SIZE = 1080;
const BG = "#0c0b0a";
const BRASS = "#cfa052";
const IVORY = "#e8e4db";
const SOFT = "#9a958a";
const HUB_URL = "myangelsquad.com";

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function drawBackground(ctx) {
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // soft radial glow at top
    const grad = ctx.createRadialGradient(SIZE / 2, 120, 0, SIZE / 2, 120, 700);
    grad.addColorStop(0, "rgba(207,160,82,0.18)");
    grad.addColorStop(1, "rgba(207,160,82,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // hairline border
    ctx.strokeStyle = BRASS;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(30, 30, SIZE - 60, SIZE - 60);
}

function drawWordmark(ctx) {
    // brass dot
    ctx.fillStyle = BRASS;
    ctx.beginPath();
    ctx.arc(96, 96, 8, 0, Math.PI * 2);
    ctx.fill();
    // wordmark
    ctx.fillStyle = IVORY;
    ctx.font = "300 28px 'Cormorant Garamond', Georgia, serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText("Angel ", 116, 96);
    ctx.font = "italic 300 28px 'Cormorant Garamond', Georgia, serif";
    ctx.fillStyle = BRASS;
    const off = ctx.measureText("Angel ").width;
    ctx.fillText("Squad", 116 + off, 96);
}

function drawArtwork(ctx, img) {
    const boxX = 190;
    const boxY = 170;
    const boxW = 700;
    const boxH = 700;

    // Brass frame
    ctx.strokeStyle = BRASS;
    ctx.lineWidth = 2;
    ctx.strokeRect(boxX - 4, boxY - 4, boxW + 8, boxH + 8);

    // Fit image inside box (contain)
    const ratio = Math.min(boxW / img.width, boxH / img.height);
    const w = img.width * ratio;
    const h = img.height * ratio;
    const x = boxX + (boxW - w) / 2;
    const y = boxY + (boxH - h) / 2;

    // Dark inner fill so contained images don't reveal page background
    ctx.fillStyle = "#151311";
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.drawImage(img, x, y, w, h);
}

function drawTextBlock(ctx, number, memberName) {
    // Big brass numeral
    ctx.fillStyle = BRASS;
    ctx.font = "400 140px 'Cormorant Garamond', Georgia, serif";
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "center";
    ctx.fillText(number, SIZE / 2, 1010);

    // "ANGEL NUMBER" label above the numeral
    ctx.fillStyle = SOFT;
    ctx.font = "500 18px 'Outfit', system-ui, sans-serif";
    ctx.letterSpacing = "0.25em";
    ctx.fillText("A  N  G  E  L     N  U  M  B  E  R", SIZE / 2, 920);

    // "for Jane Doe"
    ctx.fillStyle = IVORY;
    ctx.font = "italic 300 38px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText(`for ${memberName}`, SIZE / 2, 1015 + 50);
}

function drawFooter(ctx) {
    ctx.fillStyle = SOFT;
    ctx.font = "500 16px 'Outfit', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(HUB_URL, SIZE / 2, SIZE - 60);
}

async function buildCardBlob({ angel, memberName, variant = "designed" }) {
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d");

    drawBackground(ctx);
    drawWordmark(ctx);

    const useSketch = variant === "sketch" && angel.placeholderImageUrl;
    const sourceUrl = useSketch ? angel.placeholderImageUrl : angel.mainImageUrl;
    const sourceVersion = useSketch ? angel.placeholderImageVersion : angel.imageVersion;

    try {
        const img = await loadImage(withImageVersion(sourceUrl, sourceVersion));
        drawArtwork(ctx, img);
    } catch {
        // Skip artwork on failure; card still ships with the numeral.
        ctx.fillStyle = "#151311";
        ctx.fillRect(190, 170, 700, 700);
    }

    drawTextBlock(ctx, angel.number, memberName);
    drawFooter(ctx);

    return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

/**
 * Generate and offer to share/download the social card for an angel.
 * On capable mobile browsers this opens the native share sheet; otherwise
 * it falls back to a regular file download.
 *
 * @param {object} args
 * @param {object} args.angel
 * @param {string} args.memberName
 * @param {"designed"|"sketch"} [args.variant="designed"]
 */
export async function shareAngelCard({ angel, memberName, variant = "designed" }) {
    const blob = await buildCardBlob({ angel, memberName, variant });
    if (!blob) throw new Error("Could not build the share card.");

    const safe = (memberName || "member").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const variantTag = variant === "sketch" ? "-sketch" : "";
    const filename = `angel-${angel.number}${variantTag}-${safe}.png`;
    return await saveOrShareBlob({
        blob,
        filename,
        mimeType: "image/png",
        title: `Angel ${angel.number}`,
        text: `My Angel from the Squad — № ${angel.number}.`,
    });
}
